
import { useState, useEffect } from 'react';
import { ServiceRegistry } from '@/services/ServiceRegistry';
import { useAuth } from '@/context/AuthContext';
import { formatAmount } from "@financialharmony/ui";

interface FinancialBalances {
  bankBalance: string;
  payablesBalance: string;
  receivablesBalance: string;
  loadingBankBalance: boolean;
  loadingPayablesBalance: boolean;
  loadingReceivablesBalance: boolean;
}

export const useFinancialBalances = (currency: string = 'SEK'): FinancialBalances => {
  const { user } = useAuth();
  const [bankBalance, setBankBalance] = useState<string>("$");
  const [payablesBalance, setPayablesBalance] = useState<string>("$");
  const [receivablesBalance, setReceivablesBalance] = useState<string>("$");
  const [loadingBankBalance, setLoadingBankBalance] = useState<boolean>(true);
  const [loadingPayablesBalance, setLoadingPayablesBalance] = useState<boolean>(true);
  const [loadingReceivablesBalance, setLoadingReceivablesBalance] = useState<boolean>(true);

  // Load bank balance
  useEffect(() => {
    const fetchBankBalance = async () => {
      if (!user) {
        setLoadingBankBalance(false);
        setBankBalance("$");
        return;
      }
      setLoadingBankBalance(true);
      try {
        const response = await ServiceRegistry.getAccountService().getBankBalance(currency);
        setBankBalance(`${currency} ${formatAmount(response.balance)}`);
      } catch (error: any) {
        console.error("Error loading bank balance:", error);

        setBankBalance("$");
      } finally {
        setLoadingBankBalance(false);
      }
    };
    fetchBankBalance();
  }, [user, currency]);

  // Load payables balance
  useEffect(() => {
    const fetchPayablesBalance = async () => {
      if (!user) {
        setLoadingPayablesBalance(false);
        setPayablesBalance("$");
        return;
      }
      setLoadingPayablesBalance(true);
      try {
        const response = await ServiceRegistry.getPaymentService().getPendingPayablesBalance(currency);
        setPayablesBalance(`${currency} ${formatAmount(response.balance)}`);
      } catch (error) {
        setPayablesBalance("$");
        console.error("Error loading payables balance:", error);
      } finally {
        setLoadingPayablesBalance(false);
      }
    };
    fetchPayablesBalance();
  }, [user, currency]);

  // Load receivables balance
  useEffect(() => {
    const fetchReceivablesBalance = async () => {
      if (!user) {
        setLoadingReceivablesBalance(false);
        setReceivablesBalance("$");
        return;
      }
      setLoadingReceivablesBalance(true);
      try {
        const response = await ServiceRegistry.getReceivableService().getPendingReceivablesBalance(currency);
        setReceivablesBalance(`${currency} ${formatAmount(response.balance)}`);
      } catch (error) {
        setReceivablesBalance("$");
        console.error("Error loading receivables balance:", error);
      } finally {
        setLoadingReceivablesBalance(false);
      }
    };
    fetchReceivablesBalance();
  }, [user, currency]);

  return {
    bankBalance,
    payablesBalance,
    receivablesBalance,
    loadingBankBalance,
    loadingPayablesBalance,
    loadingReceivablesBalance,
  };
};
